/**
 * PR-AdminUsers-7 — /settings/profile
 * Perfil propio: ver segmentos/áreas/roles asignados y editar telegram_chat_id.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMyProfile, useUpdateMyProfile } from "@/hooks/useMe";

export function SettingsProfilePage() {
  const profile = useMyProfile();
  const updateProfile = useUpdateMyProfile();
  const [telegram, setTelegram] = useState<string>("");
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (profile.isPending) return <p className="text-sm text-muted-foreground">Cargando…</p>;
  if (profile.isError) return <p className="text-sm text-destructive">Error al cargar perfil.</p>;

  const p = profile.data;

  const handleEdit = () => {
    setTelegram(p.telegram_chat_id ?? "");
    setEditing(true);
    setSaved(false);
  };

  const handleSave = async () => {
    setError(null);
    try {
      await updateProfile.mutateAsync({ telegram_chat_id: telegram.trim() || null });
      setEditing(false);
      setSaved(true);
    } catch (e) {
      if (e instanceof ApiError) setError(e.message);
    }
  };

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <h1 className="text-2xl font-semibold">Mi perfil</h1>
        <p className="text-sm text-muted-foreground">Tu información y permisos asignados.</p>
      </div>

      {/* must_change_password banner */}
      {p.must_change_password && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
          Tu contraseña fue reseteada por un administrador.{" "}
          <Link to="/settings/security" className="underline font-medium">
            Cámbiala ahora.
          </Link>
        </div>
      )}

      {/* Datos básicos */}
      <section className="space-y-4">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Información
        </h2>
        <div className="rounded-lg border bg-card divide-y">
          <Row label="Username" value={p.username} />
          <Row label="Rol" value={p.role} />
          <Row label="Estado" value={p.is_active ? "Activo" : "Inactivo"} />
          <Row label="2FA" value={p.has_2fa ? "Activo" : "No configurado"} />
          <Row
            label="Creado"
            value={new Date(p.created_at).toLocaleDateString("es-MX")}
          />
          {p.last_login_at && (
            <Row
              label="Último login"
              value={new Date(p.last_login_at).toLocaleString("es-MX")}
            />
          )}
          {/* Telegram editable */}
          <div className="flex items-center justify-between px-4 py-3 text-sm">
            <span className="text-muted-foreground w-40">Telegram Chat ID</span>
            {editing ? (
              <div className="flex items-center gap-2 flex-1">
                <Input
                  value={telegram}
                  onChange={e => setTelegram(e.target.value)}
                  className="h-8 flex-1"
                  placeholder="Ej. 123456789"
                  onKeyDown={e => e.key === "Enter" && void handleSave()}
                  autoFocus
                />
                <Button size="sm" onClick={() => void handleSave()} disabled={updateProfile.isPending}>
                  Guardar
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                  Cancelar
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-3 flex-1">
                <span className="flex-1">{p.telegram_chat_id ?? <em className="text-muted-foreground">—</em>}</span>
                <Button size="sm" variant="ghost" onClick={handleEdit}>
                  Editar
                </Button>
              </div>
            )}
          </div>
          {error && <p className="text-xs text-destructive px-4 pb-3">{error}</p>}
          {saved && <p className="text-xs text-emerald-600 px-4 pb-3">Guardado.</p>}
        </div>
      </section>

      {/* Segmentos */}
      {p.segments.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Segmentos asignados
          </h2>
          <div className="flex flex-wrap gap-2">
            {p.segments.map(s => (
              <span key={s} className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                {s}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Áreas */}
      {p.area_roles.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Áreas y roles
          </h2>
          <div className="rounded-lg border divide-y text-sm">
            {p.area_roles.map(ar => (
              <div key={ar.area_id} className="flex items-center justify-between px-4 py-2.5">
                <span>{ar.area_name}</span>
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium capitalize">
                  {ar.role === "manager" ? "Manager" : "Operador"}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Roles globales */}
      {p.global_roles.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Roles globales
          </h2>
          <div className="flex flex-wrap gap-2">
            {p.global_roles.map(r => (
              <span key={r} className="flex items-center gap-1.5 rounded-full bg-amber-500/15 px-3 py-1 text-xs font-medium text-amber-700 dark:text-amber-400">
                <ShieldCheck className="size-3" /> {r}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Accesos rápidos */}
      <section className="flex gap-3 pt-2">
        <Link to="/settings/security">
          <Button variant="outline" size="sm">Cambiar contraseña / 2FA</Button>
        </Link>
      </section>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center px-4 py-3 text-sm">
      <span className="text-muted-foreground w-40">{label}</span>
      <span>{value}</span>
    </div>
  );
}
